import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  GraduationCap,
  Plus,
  Edit,
  Trash2,
  ChevronLeft,
  CheckCircle2,
  Clock as ClockIcon,
  Circle,
  Users,
  UserCheck,
  Upload,
  FileUp,
  FileText,
  Download,
  X,
} from "lucide-react";

const TrainingPage = () => {
  const { token, user, hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [pripravniki, setPripravniki] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // ----- Catalog search/filter (persisted) -----
  const CAT_FILTER_KEY = "training_catalog_filter_v1";
  const [catalogFilter, setCatalogFilter] = useState(() => {
    try { return localStorage.getItem(`${CAT_FILTER_KEY}_text`) || ""; } catch { return ""; }
  });
  const [catalogCategoryFilter, setCatalogCategoryFilter] = useState(() => {
    try { return localStorage.getItem(`${CAT_FILTER_KEY}_cat`) || ""; } catch { return ""; }
  });
  useEffect(() => {
    try {
      localStorage.setItem(`${CAT_FILTER_KEY}_text`, catalogFilter);
      localStorage.setItem(`${CAT_FILTER_KEY}_cat`, catalogCategoryFilter);
    } catch { /* ignore */ }
  }, [catalogFilter, catalogCategoryFilter]);
  const [itemForm, setItemForm] = useState({
    naslov: "",
    opis: "",
    kategorija: "splošno",
    vrstni_red: 0,
    predvidene_ure: "",
    naloge: [],
  });
  const [pdfUploading, setPdfUploading] = useState(false);
  const [ldDocs, setLdDocs] = useState([]);
  const [docUploading, setDocUploading] = useState(null);
  const [deleteItemConfirm, setDeleteItemConfirm] = useState(null);
  // Pripravnik management
  const [addPripDlg, setAddPripDlg] = useState(false);
  const [eligibleMembers, setEligibleMembers] = useState([]);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [eligibleSearch, setEligibleSearch] = useState("");

  const isCatalogManager = hasRole([
    "administrator",
    "predsednik",
    "tajnik",
    "gospodar",
    "komisija_izobrazevanje",
  ]);
  const isApprover = hasRole([
    "administrator",
    "predsednik",
    "gospodar",
    "komisija_izobrazevanje",
  ]);
  const isPripravnik = user?.vloga === "pripravnik";

  useEffect(() => {
    fetchAll();
  }, []);  

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [itemsRes, pripravnikiRes, docsRes] = await Promise.all([
        axios.get(`${API}/training/items`, { headers }),
        axios.get(`${API}/training/pripravniki`, { headers }),
        axios.get(`${API}/training/documents`, { headers }),
      ]);
      setItems(itemsRes.data);
      setPripravniki(pripravnikiRes.data);
      setLdDocs(docsRes.data);
      // If pripravnik themselves, auto-open their own plan
      if (user?.vloga === "pripravnik" && pripravnikiRes.data.length === 1) {
        await openPlan(pripravnikiRes.data[0].id);
      }
    } catch (e) {
      toast.error("Napaka pri nalaganju");
    } finally {
      setLoading(false);
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Izberite PDF datoteko");
      return;
    }
    const naslov = window.prompt("Vnesite naslov dokumenta (npr. Pravilnik, Poslovnik, Statut):", file.name.replace(/\.pdf$/i, ""));
    if (!naslov || !naslov.trim()) return;
    setDocUploading("upload");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/training/documents?naslov=${encodeURIComponent(naslov.trim())}`, formData, { headers });
      toast.success("Dokument naložen");
      const docsRes = await axios.get(`${API}/training/documents`, { headers });
      setLdDocs(docsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Napaka");
    } finally {
      setDocUploading(null);
    }
  };

  const handleDocDownload = async (docId, filename) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/training/documents/${docId}/download`, { headers });
      const byteChars = atob(res.data.vsebina_b64);
      const bytes = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.data.filename || filename || "dokument.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Napaka pri prenosu");
    }
  };

  const handleDocDelete = async (docId) => {
    if (!window.confirm("Izbrišem dokument?")) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/training/documents/${docId}`, { headers });
      toast.success("Dokument izbrisan");
      const docsRes = await axios.get(`${API}/training/documents`, { headers });
      setLdDocs(docsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Napaka");
    }
  };

  const openAddPripravnik = async () => {
    setAddPripDlg(true);
    setEligibleLoading(true);
    setEligibleSearch("");
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/training/eligible-members`, { headers });
      setEligibleMembers(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri nalaganju članov");
    } finally {
      setEligibleLoading(false);
    }
  };

  const promoteToPripravnik = async (userId, ime) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/training/pripravniki/${userId}/promote`, {}, { headers });
      toast.success(`${ime} je zdaj pripravnik`);
      setAddPripDlg(false);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    }
  };

  const demotePripravnik = async (userId, ime) => {
    if (!window.confirm(`Odstrani ${ime} iz pripravništva? Postal bo navadni član.`)) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/training/pripravniki/${userId}/demote`, {}, { headers });
      toast.success(`${ime} ni več pripravnik`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    }
  };

  const openPlan = async (userId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/training/plan/${userId}`, { headers });
      setSelectedPlan(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    }
  };

  const refreshPlan = async () => {
    if (selectedPlan?.user?.id) {
      await openPlan(selectedPlan.user.id);
      // also refresh list counts
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/training/pripravniki`, { headers });
        setPripravniki(res.data);
      } catch (e) {
        console.warn("[TrainingPage] Failed to refresh pripravniki list counts:", e);
      }
    }
  };

  // ----- Catalog management -----
  const openCreateItem = () => {
    setEditingItem(null);
    setItemForm({ naslov: "", opis: "", kategorija: "splošno", vrstni_red: items.length + 1, predvidene_ure: "", naloge: [] });
    setItemDialogOpen(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      naslov: item.naslov,
      opis: item.opis || "",
      kategorija: item.kategorija || "splošno",
      vrstni_red: item.vrstni_red || 0,
      predvidene_ure: item.predvidene_ure != null ? item.predvidene_ure : "",
      naloge: Array.isArray(item.naloge) ? item.naloge.map(n => ({ id: n.id, naslov: n.naslov, ure: n.ure != null ? n.ure : "", obvezno: n.obvezno !== false })) : [],
    });
    setItemDialogOpen(true);
  };

  const addNaloga = (obvezno = true) => {
    setItemForm(f => ({ ...f, naloge: [...(f.naloge || []), { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, naslov: "", ure: "", obvezno }] }));
  };

  const updateNaloga = (idx, field, value) => {
    setItemForm(f => {
      const next = [...(f.naloge || [])];
      next[idx] = { ...next[idx], [field]: value };
      return { ...f, naloge: next };
    });
  };

  const removeNaloga = (idx) => {
    setItemForm(f => ({ ...f, naloge: (f.naloge || []).filter((_, i) => i !== idx) }));
  };

  const saveItem = async (e) => {
    e.preventDefault();
    if (!itemForm.naslov.trim()) {
      toast.error("Naslov je obvezen");
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...itemForm,
        predvidene_ure: itemForm.predvidene_ure === "" || itemForm.predvidene_ure == null ? null : parseFloat(itemForm.predvidene_ure),
        naloge: (itemForm.naloge || [])
          .filter(n => (n.naslov || "").trim())
          .map(n => ({
            id: n.id,
            naslov: n.naslov,
            ure: n.ure === "" || n.ure == null ? null : parseFloat(n.ure),
            obvezno: n.obvezno !== false,
          })),
      };
      if (editingItem) {
        await axios.put(`${API}/training/items/${editingItem.id}`, payload, { headers });
        toast.success("Postavka posodobljena");
      } else {
        await axios.post(`${API}/training/items`, payload, { headers });
        toast.success("Postavka dodana");
      }
      setItemDialogOpen(false);
      const res = await axios.get(`${API}/training/items`, { headers });
      setItems(res.data);
      if (selectedPlan) await refreshPlan();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    }
  };

  const deleteItem = (item) => {
    setDeleteItemConfirm(item);
  };

  const confirmDeleteItem = async () => {
    const item = deleteItemConfirm;
    if (!item) return;
    setDeleteItemConfirm(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/training/items/${item.id}`, { headers });
      toast.success("Postavka izbrisana");
      const res = await axios.get(`${API}/training/items`, { headers });
      setItems(res.data);
      if (selectedPlan) await refreshPlan();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri brisanju");
    }
  };

  const handlePdfImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset
    if (!file) return;
    const name = file.name.toLowerCase();
    const supportedExt = [".pdf", ".docx", ".xlsx", ".xls", ".csv"];
    if (!supportedExt.some(ext => name.endsWith(ext))) {
      toast.error("Podprti formati: PDF, DOCX, XLSX, CSV");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Datoteka je prevelika (max 20 MB)");
      return;
    }
    setPdfUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/training/items/import-pdf`, formData, { headers });
      toast.success(
        `Uvoženih: ${res.data.count_imported}. Preskočenih (duplikati): ${res.data.count_skipped_existing}.`
      );
      const itemsRes = await axios.get(`${API}/training/items`, { headers });
      setItems(itemsRes.data);
      if (selectedPlan) await refreshPlan();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Napaka pri uvozu");
    } finally {
      setPdfUploading(false);
    }
  };

  // ----- Mark/Approve/Reject -----
  const markDone = async (itemId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/training/plan/${selectedPlan.user.id}/items/${itemId}/mark`,
        { opomba: "" },
        { headers }
      );
      toast.success("Označeno - čaka na potrditev mentorja");
      await refreshPlan();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    }
  };

  const unmark = async (itemId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/training/plan/${selectedPlan.user.id}/items/${itemId}/unmark`, {}, { headers });
      toast.success("Oznaka odstranjena");
      await refreshPlan();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    }
  };

  const toggleSubtask = async (itemId, nalId, done) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/training/plan/${selectedPlan.user.id}/items/${itemId}/subtask`,
        { nal_id: nalId, done },
        { headers }
      );
      await refreshPlan();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri podnalogi");
    }
  };

  const exportTrainingPlan = async (format) => {
    if (!selectedPlan) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(
        `${API}/training/plan/${selectedPlan.user.id}/export?format=${format}`,
        { headers, responseType: "blob" }
      );
      const mime = format === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = `${selectedPlan.user.ime || ""}-${selectedPlan.user.priimek || ""}`.trim().replace(/\s+/g, "-");
      a.download = `izobrazevanje-${safeName}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Izvoz uspešen (${format.toUpperCase()})`);
    } catch (e) {
      toast.error("Napaka pri izvozu");
    }
  };

  const approve = async (itemId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/training/plan/${selectedPlan.user.id}/items/${itemId}/approve`, {}, { headers });
      toast.success("Potrjeno");
      await refreshPlan();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    }
  };

  const reject = async (itemId) => {
    if (!window.confirm("Zavrnem označeno postavko?")) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/training/plan/${selectedPlan.user.id}/items/${itemId}/reject`, {}, { headers });
      toast.success("Zavrnjeno");
      await refreshPlan();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    }
  };

  const renderDocumentsCard = () => (
    <Card data-testid="ld-documents-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Dokumenti LD
          </CardTitle>
          <CardDescription>Pravilnik, poslovnik, statut ali kateri koli drug dokument, ki ga LD deli s člani.</CardDescription>
        </div>
        {isCatalogManager && (
          <div>
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              id="doc-upload-input"
              onChange={handleDocUpload}
              disabled={docUploading === "upload"}
              data-testid="doc-upload-input"
            />
            <Button
              size="sm"
              onClick={() => document.getElementById("doc-upload-input")?.click()}
              disabled={docUploading === "upload"}
              data-testid="doc-upload-btn"
            >
              <Upload className="h-3.5 w-3.5 mr-1" />
              {docUploading === "upload" ? "Nalagam..." : "Naloži dokument"}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {ldDocs.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-4">
            Ni naloženih dokumentov.
            {isCatalogManager && " Kliknite `Naloži dokument` za dodajanje pravilnika, poslovnika ali statuta."}
          </div>
        )}
        {ldDocs.map(d => (
          <div key={d.id} className="p-3 rounded-lg border flex items-center gap-3 flex-wrap justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-5 w-5 shrink-0 text-primary" />
              <div className="min-w-0">
                <div className="font-medium text-sm">{d.naslov}</div>
                <div className="text-xs text-muted-foreground break-words">
                  {d.filename} • {d.size ? `${(d.size / 1024).toFixed(0)} KB` : ""}
                  {d.uploaded_by && ` • ${d.uploaded_by}`}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="sm" variant="outline" onClick={() => handleDocDownload(d.id, d.filename)} data-testid={`download-${d.id}`}>
                <Download className="h-3.5 w-3.5 mr-1" />
                Prenos
              </Button>
              {isCatalogManager && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-red-600"
                  onClick={() => handleDocDelete(d.id)}
                  data-testid={`doc-delete-${d.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  // Computed catalog categories + filtered items
  const catalogCategories = Array.from(new Set(items.map(it => it.kategorija).filter(Boolean))).sort();
  const filterText = (catalogFilter || "").toLowerCase().trim();
  const itemsFiltered = items.filter(it => {
    if (catalogCategoryFilter && it.kategorija !== catalogCategoryFilter) return false;
    if (filterText) {
      const naslov = (it.naslov || "").toLowerCase();
      const opis = (it.opis || "").toLowerCase();
      if (!naslov.includes(filterText) && !opis.includes(filterText)) return false;
    }
    return true;
  });

  // ===== Single plan view =====
  if (selectedPlan) {
    const u = selectedPlan.user;
    const plan = selectedPlan.plan || [];
    const completedCount = plan.filter(p => p.status === "potrjeno").length;
    const pendingCount = plan.filter(p => p.status === "caka").length;
    const isOwnPlan = u.id === user?.id;
    const canManageThisPlan = isApprover || u.mentor_id === user?.id || u.mentor_id_2 === user?.id;

    // ----- Compute hour summary -----
    // Split into obvezne and dodatne ure based on `obvezno` flag on each subtask.
    // For postavke without subtasks: predvidene_ure are considered obvezne.
    let obvTotal = 0, obvDone = 0, dodTotal = 0, dodDone = 0;
    plan.forEach(p => {
      const naloge = p.naloge || [];
      const done = new Set(p.done_naloge_ids || []);
      const isApproved = p.status === "potrjeno";

      if (naloge.length === 0) {
        const ure = Number(p.predvidene_ure) || 0;
        obvTotal += ure;
        if (isApproved) obvDone += ure;
        return;
      }

      // Sum from subtasks
      let pObvT = 0, pObvD = 0, pDodT = 0, pDodD = 0;
      naloge.forEach(n => {
        const ure = Number(n.ure) || 0;
        const obvezno = n.obvezno !== false;
        if (obvezno) {
          pObvT += ure;
          if (done.has(n.id) || isApproved) pObvD += ure;
        } else {
          pDodT += ure;
          if (done.has(n.id) || isApproved) pDodD += ure;
        }
      });
      obvTotal += pObvT;
      obvDone += pObvD;
      dodTotal += pDodT;
      dodDone += pDodD;
    });
    const totalHours = obvTotal + dodTotal;
    const doneHours = obvDone + dodDone;
    const showHourSummary = totalHours > 0;

    return (
      <div className="space-y-4 animate-fade-in" data-testid="training-plan-view">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3">
            {pripravniki.length > 1 && (
              <Button variant="ghost" size="sm" onClick={() => setSelectedPlan(null)}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Seznam
              </Button>
            )}
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-primary flex items-center gap-2">
                <GraduationCap className="h-6 w-6" />
                {u.ime} {u.priimek}
              </h1>
              {(u.mentor_ime || u.mentor_ime_2) && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs">
                  {u.mentor_ime && (
                    <span className="text-muted-foreground">
                      Mentor 1: <strong className="text-foreground">{u.mentor_ime}</strong>
                      {u.mentor_telefon && (
                        <a href={`tel:${u.mentor_telefon}`} className="ml-1 text-primary hover:underline">
                          {u.mentor_telefon}
                        </a>
                      )}
                    </span>
                  )}
                  {u.mentor_ime_2 && (
                    <span className="text-muted-foreground">
                      Mentor 2: <strong className="text-foreground">{u.mentor_ime_2}</strong>
                      {u.mentor_telefon_2 && (
                        <a href={`tel:${u.mentor_telefon_2}`} className="ml-1 text-primary hover:underline">
                          {u.mentor_telefon_2}
                        </a>
                      )}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Badge className="bg-green-100 text-green-800">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              {completedCount}/{plan.length} potrjenih
            </Badge>
            {pendingCount > 0 && (
              <Badge className="bg-amber-100 text-amber-800">
                <ClockIcon className="h-3 w-3 mr-1" />
                {pendingCount} čaka
              </Badge>
            )}
            {(canManageThisPlan || isOwnPlan) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="export-training-btn">
                    <Download className="h-3.5 w-3.5 mr-1" />
                    Izvozi
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => exportTrainingPlan("xlsx")} data-testid="export-training-xlsx">
                    <FileText className="h-4 w-4 mr-2 text-green-600" />
                    Excel (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportTrainingPlan("pdf")} data-testid="export-training-pdf">
                    <FileText className="h-4 w-4 mr-2 text-red-600" />
                    PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Hour summary card */}
        {showHourSummary && (
          <Card data-testid="hour-summary-card" className="border-emerald-200 bg-emerald-50/40">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm">
                  <ClockIcon className="h-5 w-5 text-emerald-700" />
                  <span className="font-semibold text-emerald-800">Predvidene ure izobraževanja</span>
                </div>
                <div className="flex items-center gap-3 text-sm tabular-nums">
                  <span className="text-emerald-700 font-semibold">{doneHours} h</span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-foreground font-semibold">{totalHours} h</span>
                  <Badge variant="outline" className="text-[10px]">
                    {totalHours > 0 ? Math.round((doneHours / totalHours) * 100) : 0}%
                  </Badge>
                </div>
              </div>
              <div className="mt-2 h-2 bg-emerald-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-600 transition-all duration-500"
                  style={{ width: `${totalHours > 0 ? Math.min(100, (doneHours / totalHours) * 100) : 0}%` }}
                />
              </div>
              {(obvTotal > 0 || dodTotal > 0) && (
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  {obvTotal > 0 && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2" data-testid="hour-obv-block">
                      <div className="text-amber-900 font-semibold mb-0.5">Obvezne ure</div>
                      <div className="tabular-nums text-amber-800">
                        <span className="font-bold">{obvDone} h</span> / {obvTotal} h
                        <span className="ml-2 text-[10px] text-amber-700">
                          ({obvTotal > 0 ? Math.round((obvDone / obvTotal) * 100) : 0}%)
                        </span>
                      </div>
                    </div>
                  )}
                  {dodTotal > 0 && (
                    <div className="rounded-md border border-blue-300 bg-blue-50 px-3 py-2" data-testid="hour-dod-block">
                      <div className="text-blue-900 font-semibold mb-0.5">Dodatne ure</div>
                      <div className="tabular-nums text-blue-800">
                        <span className="font-bold">{dodDone} h</span> / {dodTotal} h
                        <span className="ml-2 text-[10px] text-blue-700">
                          ({dodTotal > 0 ? Math.round((dodDone / dodTotal) * 100) : 0}%)
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {plan.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <GraduationCap className="h-10 w-10 mx-auto mb-2 opacity-40" />
              Plan izobraževanja še ni vzpostavljen. Predsednik/gospodar/komisija lahko doda postavke.
              {isCatalogManager && (
                <div className="mt-3">
                  <Button onClick={openCreateItem} data-testid="create-first-item">
                    <Plus className="h-4 w-4 mr-1" />
                    Dodaj prvo postavko
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle className="text-base">Postavke izobraževanja</CardTitle>
                {isCatalogManager && (
                  <Button size="sm" variant="outline" onClick={openCreateItem} data-testid="add-training-item">
                    <Plus className="h-4 w-4 mr-1" />
                    Dodaj postavko
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-260px)] min-h-[400px] max-h-[700px] px-4 sm:px-6 py-3">
                <div className="space-y-2">
                  {plan.map(p => {
                    const isPotrjeno = p.status === "potrjeno";
                    const isCaka = p.status === "caka";
                    return (
                      <div
                        key={p.id}
                        className={`p-3 rounded-lg border flex items-start justify-between gap-3 ${
                          isPotrjeno ? "bg-green-50/60 border-green-200" :
                          isCaka ? "bg-amber-50/60 border-amber-200" :
                          "bg-card"
                        }`}
                        data-testid={`plan-item-${p.id}`}
                      >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="pt-0.5">
                        {(isOwnPlan && !isPotrjeno) || (canManageThisPlan && !isOwnPlan) ? (
                          <Checkbox
                            checked={isPotrjeno || isCaka}
                            onCheckedChange={(checked) => {
                              if (checked) markDone(p.id);
                              else unmark(p.id);
                            }}
                            data-testid={`check-${p.id}`}
                          />
                        ) : isPotrjeno ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : isCaka ? (
                          <ClockIcon className="h-5 w-5 text-amber-600" />
                        ) : (
                          <Circle className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm break-words">{p.naslov}</span>
                          {p.kategorija && (
                            <Badge variant="outline" className="text-[10px]">{p.kategorija}</Badge>
                          )}
                          {p.predvidene_ure != null && p.predvidene_ure > 0 && (
                            <Badge variant="outline" className="text-[10px] gap-0.5">
                              <ClockIcon className="h-2.5 w-2.5" />
                              {p.predvidene_ure}h
                            </Badge>
                          )}
                          {isPotrjeno && (
                            <Badge className="bg-green-100 text-green-800 text-[10px]">Potrjeno</Badge>
                          )}
                          {isCaka && (
                            <Badge className="bg-amber-100 text-amber-800 text-[10px]">Čaka potrditev</Badge>
                          )}
                        </div>
                        {p.opis && <p className="text-xs text-muted-foreground mt-1 break-words">{p.opis}</p>}
                        {Array.isArray(p.naloge) && p.naloge.length > 0 && (
                          <div className="mt-2 ml-1 space-y-1.5 border-l-2 border-muted pl-3" data-testid={`naloge-${p.id}`}>
                            {p.naloge.map((nal) => {
                              const isDone = (p.done_naloge_ids || []).includes(nal.id);
                              const isObvezna = nal.obvezno !== false;
                              const canToggle = (isOwnPlan && !isPotrjeno) || (canManageThisPlan && !isOwnPlan);
                              return (
                                <div key={nal.id} className={`flex items-start gap-2 rounded px-1.5 py-0.5 ${isObvezna ? "" : "bg-blue-50/40"}`}>
                                  {canToggle ? (
                                    <Checkbox
                                      checked={isDone}
                                      onCheckedChange={(c) => toggleSubtask(p.id, nal.id, !!c)}
                                      className="mt-0.5"
                                      data-testid={`subtask-${p.id}-${nal.id}`}
                                    />
                                  ) : isDone ? (
                                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                                  ) : (
                                    <Circle className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                                  )}
                                  {!isObvezna && (
                                    <Badge variant="outline" className="text-[9px] h-4 px-1 border-blue-400 text-blue-700 shrink-0">DOD</Badge>
                                  )}
                                  <span className={`text-xs break-words flex-1 ${isDone ? "line-through text-muted-foreground" : ""}`}>
                                    {nal.naslov}
                                  </span>
                                  {nal.ure != null && nal.ure > 0 && (
                                    <span className="text-[10px] text-muted-foreground tabular-nums shrink-0 flex items-center gap-0.5">
                                      <ClockIcon className="h-2.5 w-2.5" />
                                      {nal.ure}h
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                            {(() => {
                              const obvNaloge = (p.naloge || []).filter(n => n.obvezno !== false);
                              const dodNaloge = (p.naloge || []).filter(n => n.obvezno === false);
                              const done = new Set(p.done_naloge_ids || []);
                              const obvTotal = obvNaloge.reduce((s, n) => s + (Number(n.ure) || 0), 0);
                              const obvDone = obvNaloge.filter(n => done.has(n.id)).reduce((s, n) => s + (Number(n.ure) || 0), 0);
                              const dodTotal = dodNaloge.reduce((s, n) => s + (Number(n.ure) || 0), 0);
                              const dodDone = dodNaloge.filter(n => done.has(n.id)).reduce((s, n) => s + (Number(n.ure) || 0), 0);
                              const totalCnt = (p.naloge || []).length;
                              const doneCnt = (p.done_naloge_ids || []).length;
                              return (
                                <div className="text-[10px] text-muted-foreground mt-1 flex items-center justify-between flex-wrap gap-x-3 gap-y-0.5">
                                  <span>{doneCnt}/{totalCnt} opravljenih</span>
                                  <div className="flex items-center gap-2 tabular-nums">
                                    {obvTotal > 0 && (
                                      <span className="text-amber-700">
                                        Obv: {obvDone}h / {obvTotal}h
                                      </span>
                                    )}
                                    {dodTotal > 0 && (
                                      <span className="text-blue-700">
                                        Dod: {dodDone}h / {dodTotal}h
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        {isPotrjeno && p.potrdil_ime && (
                          <p className="text-[11px] text-green-700 mt-1">
                            Potrdil: {p.potrdil_ime}
                            {p.potrjeno_datum && ` • ${new Date(p.potrjeno_datum).toLocaleDateString("sl-SI")}`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isCaka && canManageThisPlan && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:bg-green-50 hover:text-green-700"
                            onClick={() => approve(p.id)}
                            data-testid={`approve-${p.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => reject(p.id)}
                            data-testid={`reject-${p.id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {isCatalogManager && (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => openEditItem(p)}
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-600"
                            onClick={() => deleteItem(p)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Item dialog */}
        <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
          <DialogContent className="max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Uredi postavko" : "Nova postavka"}</DialogTitle>
              <DialogDescription>Postavka v skupnem planu izobraževanja za vse pripravnike.</DialogDescription>
            </DialogHeader>
            <form onSubmit={saveItem} className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Naslov *</Label>
                <Input
                  value={itemForm.naslov}
                  onChange={(e) => setItemForm({ ...itemForm, naslov: e.target.value })}
                  required
                  data-testid="item-naslov"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Kategorija</Label>
                <Input
                  value={itemForm.kategorija}
                  onChange={(e) => setItemForm({ ...itemForm, kategorija: e.target.value })}
                  placeholder="npr. teorija, praksa, izpit"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Opis</Label>
                <Textarea
                  value={itemForm.opis}
                  onChange={(e) => setItemForm({ ...itemForm, opis: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Vrstni red</Label>
                <Input
                  type="number"
                  value={itemForm.vrstni_red}
                  onChange={(e) => setItemForm({ ...itemForm, vrstni_red: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Predvidene ure za to temo</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  placeholder="npr. 8"
                  value={itemForm.predvidene_ure ?? ""}
                  onChange={(e) => setItemForm({ ...itemForm, predvidene_ure: e.target.value })}
                  data-testid="item-predvidene-ure-1"
                />
              </div>
              <div className="space-y-1.5 border-t pt-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Label className="text-xs">Podnaloge (npr. izdelava solnice, zalaganje, obhod...)</Label>
                  <div className="flex gap-1">
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => addNaloga(true)} data-testid="add-naloga-obvezna-1">
                      <Plus className="h-3 w-3 mr-1" />
                      Obvezna
                    </Button>
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => addNaloga(false)} data-testid="add-naloga-dodatna-1">
                      <Plus className="h-3 w-3 mr-1" />
                      Dodatna
                    </Button>
                  </div>
                </div>
                {(itemForm.naloge || []).length === 0 ? (
                  <p className="text-[11px] text-muted-foreground italic">Brez podnalog — postavka se zaključi z eno potrditvijo.</p>
                ) : (
                  <div className="space-y-1.5">
                    {(itemForm.naloge || []).map((n, idx) => {
                      const isObvezna = n.obvezno !== false;
                      return (
                        <div key={n.id || idx} className={`flex items-center gap-1.5 rounded-md px-1.5 py-1 ${isObvezna ? "bg-amber-50/60" : "bg-blue-50/60"}`}>
                          <span className="text-[11px] text-muted-foreground w-4 text-right">{idx + 1}.</span>
                          <button
                            type="button"
                            onClick={() => updateNaloga(idx, "obvezno", !isObvezna)}
                            className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 transition ${isObvezna ? "bg-amber-200 text-amber-900 hover:bg-amber-300" : "bg-blue-200 text-blue-900 hover:bg-blue-300"}`}
                            title={isObvezna ? "Klikni za 'Dodatna'" : "Klikni za 'Obvezna'"}
                            data-testid={`toggle-obvezno-1-${idx}`}
                          >
                            {isObvezna ? "OBV" : "DOD"}
                          </button>
                          <Input
                            value={n.naslov}
                            onChange={(e) => updateNaloga(idx, "naslov", e.target.value)}
                            placeholder="Naslov podnaloge"
                            className="h-8 text-xs flex-1"
                          />
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            value={n.ure ?? ""}
                            onChange={(e) => updateNaloga(idx, "ure", e.target.value)}
                            placeholder="ure"
                            className="h-8 text-xs w-16"
                            title="Predvidene ure"
                          />
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => removeNaloga(idx)}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setItemDialogOpen(false)}>Prekliči</Button>
                <Button type="submit" data-testid="save-item">{editingItem ? "Posodobi" : "Ustvari"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* LD documents (Pravila + Poslovnik) */}
        {renderDocumentsCard()}

        {/* Delete confirmation dialog */}
        <Dialog open={!!deleteItemConfirm} onOpenChange={(o) => { if (!o) setDeleteItemConfirm(null); }}>
          <DialogContent className="max-w-sm mx-4" data-testid="delete-item-confirm">
            <DialogHeader>
              <DialogTitle>Izbris postavke</DialogTitle>
              <DialogDescription>
                Postavka "{deleteItemConfirm?.naslov}" bo trajno izbrisana skupaj z vsemi napredki pripravnikov. Akcije ni mogoče razveljaviti.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteItemConfirm(null)}>Prekliči</Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteItem}
                data-testid="confirm-delete-item"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Izbriši
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ===== List of pripravniki =====
  return (
    <div className="space-y-4 animate-fade-in" data-testid="training-list">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-2">
            <GraduationCap className="h-7 w-7" />
            Plan izobraževanja
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Spremljanje napredka pripravnikov
          </p>
        </div>
        {isCatalogManager && (
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="file"
              accept=".pdf,.docx,.xlsx,.xls,.csv,application/pdf"
              className="hidden"
              id="pdf-import-input"
              onChange={handlePdfImport}
              disabled={pdfUploading}
              data-testid="pdf-import-input"
            />
            <Button
              variant="outline"
              disabled={pdfUploading}
              onClick={() => document.getElementById("pdf-import-input")?.click()}
              data-testid="pdf-import-btn"
              title="Podprti formati: PDF, DOCX, XLSX, CSV"
            >
              <FileUp className="h-4 w-4 mr-1" />
              {pdfUploading ? "Uvažam..." : "Uvozi tabelo"}
            </Button>
            <Button onClick={openAddPripravnik} variant="outline" data-testid="add-pripravnik-btn">
              <Users className="h-4 w-4 mr-1" />
              Dodaj pripravnika
            </Button>
            <Button onClick={openCreateItem} data-testid="manage-catalog">
              <Plus className="h-4 w-4 mr-1" />
              Dodaj postavko
            </Button>
          </div>
        )}
      </div>

      {/* Catalog summary card for managers */}
      {isCatalogManager && items.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <GraduationCap className="h-4 w-4" />
              Teme izobraževanja ({itemsFiltered.length}/{items.length})
            </CardTitle>
            <CardDescription>
              Te postavke so zgolj za orientacijo mentorju in pripravniku.
            </CardDescription>
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <Input
                value={catalogFilter}
                onChange={(e) => setCatalogFilter(e.target.value)}
                placeholder="Iskanje po nazivu/opisu..."
                className="h-8 text-xs flex-1"
                data-testid="catalog-filter-input"
              />
              <select
                value={catalogCategoryFilter}
                onChange={(e) => setCatalogCategoryFilter(e.target.value)}
                className="h-8 px-2 text-xs rounded-md border bg-background"
                data-testid="catalog-category-filter"
              >
                <option value="">Vse kategorije</option>
                {catalogCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              {(catalogFilter || catalogCategoryFilter) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => { setCatalogFilter(""); setCatalogCategoryFilter(""); }}
                >
                  Počisti
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[420px] sm:h-[500px] pr-2">
              <div className="space-y-1.5">
                {itemsFiltered.map(it => (
                  <div key={it.id} className="flex items-center justify-between gap-2 p-2 rounded border bg-muted/30">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium break-words">{it.naslov}</div>
                      {it.opis && <div className="text-xs text-muted-foreground break-words">{it.opis}</div>}
                      {(Array.isArray(it.naloge) && it.naloge.length > 0) || (it.predvidene_ure != null && it.predvidene_ure > 0) ? (
                        <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2">
                          {Array.isArray(it.naloge) && it.naloge.length > 0 && (
                            <span>{it.naloge.length} podnalog</span>
                          )}
                          {it.predvidene_ure != null && it.predvidene_ure > 0 && (
                            <span className="flex items-center gap-0.5">
                              <ClockIcon className="h-3 w-3" />
                              {it.predvidene_ure}h
                            </span>
                          )}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Badge variant="outline" className="text-[10px]">{it.kategorija}</Badge>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditItem(it)}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => deleteItem(it)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {pripravniki.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Users className="h-10 w-10 mx-auto mb-2 opacity-40" />
            <p>{isPripravnik ? "Vaš plan še ni vzpostavljen" : "Ni pripravnikov, ki bi jih lahko videli"}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {pripravniki.map(p => {
            const total = p.skupaj || 0;
            const done = p.potrjeno_count || 0;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            return (
              <Card
                key={p.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => openPlan(p.id)}
                data-testid={`pripravnik-card-${p.id}`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between gap-2">
                    <span className="break-words">{p.ime} {p.priimek}</span>
                    <div className="flex items-center gap-1">
                      {p.caka_count > 0 && (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">
                          <ClockIcon className="h-3 w-3 mr-1" />
                          {p.caka_count} čaka
                        </Badge>
                      )}
                      {isCatalogManager && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-red-600 hover:text-red-700"
                          onClick={(e) => { e.stopPropagation(); demotePripravnik(p.id, `${p.ime} ${p.priimek}`); }}
                          title="Odstrani iz pripravništva"
                          data-testid={`demote-pripravnik-${p.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </CardTitle>
                  {(p.mentor_ime || p.mentor_ime_2) && (
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      {p.mentor_ime && (
                        <div>
                          Mentor 1: <span className="text-foreground">{p.mentor_ime}</span>
                          {p.mentor_telefon && (
                            <a href={`tel:${p.mentor_telefon}`} onClick={(e) => e.stopPropagation()} className="ml-1 text-primary hover:underline">
                              {p.mentor_telefon}
                            </a>
                          )}
                        </div>
                      )}
                      {p.mentor_ime_2 && (
                        <div>
                          Mentor 2: <span className="text-foreground">{p.mentor_ime_2}</span>
                          {p.mentor_telefon_2 && (
                            <a href={`tel:${p.mentor_telefon_2}`} onClick={(e) => e.stopPropagation()} className="ml-1 text-primary hover:underline">
                              {p.mentor_telefon_2}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Napredek</span>
                    <span className="text-xs font-medium">{done}/{total} ({pct}%)</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${pct >= 80 ? "bg-green-500" : pct >= 40 ? "bg-amber-500" : "bg-orange-500"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* LD documents (Pravila + Poslovnik) */}
      {renderDocumentsCard()}

      <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
        <DialogContent className="max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Uredi postavko" : "Nova postavka"}</DialogTitle>
            <DialogDescription>Postavka v skupnem planu izobraževanja za vse pripravnike.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveItem} className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Naslov *</Label>
              <Input
                value={itemForm.naslov}
                onChange={(e) => setItemForm({ ...itemForm, naslov: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Kategorija</Label>
              <Input
                value={itemForm.kategorija}
                onChange={(e) => setItemForm({ ...itemForm, kategorija: e.target.value })}
                placeholder="npr. teorija, praksa, izpit"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Opis</Label>
              <Textarea
                value={itemForm.opis}
                onChange={(e) => setItemForm({ ...itemForm, opis: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Vrstni red</Label>
              <Input
                type="number"
                value={itemForm.vrstni_red}
                onChange={(e) => setItemForm({ ...itemForm, vrstni_red: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Predvidene ure za to temo</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                placeholder="npr. 8"
                value={itemForm.predvidene_ure ?? ""}
                onChange={(e) => setItemForm({ ...itemForm, predvidene_ure: e.target.value })}
                data-testid="item-predvidene-ure-2"
              />
            </div>
            <div className="space-y-1.5 border-t pt-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="text-xs">Podnaloge (npr. izdelava solnice, zalaganje, obhod...)</Label>
                <div className="flex gap-1">
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => addNaloga(true)} data-testid="add-naloga-obvezna-2">
                    <Plus className="h-3 w-3 mr-1" />
                    Obvezna
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => addNaloga(false)} data-testid="add-naloga-dodatna-2">
                    <Plus className="h-3 w-3 mr-1" />
                    Dodatna
                  </Button>
                </div>
              </div>
              {(itemForm.naloge || []).length === 0 ? (
                <p className="text-[11px] text-muted-foreground italic">Brez podnalog — postavka se zaključi z eno potrditvijo.</p>
              ) : (
                <div className="space-y-1.5">
                  {(itemForm.naloge || []).map((n, idx) => {
                    const isObvezna = n.obvezno !== false;
                    return (
                      <div key={n.id || idx} className={`flex items-center gap-1.5 rounded-md px-1.5 py-1 ${isObvezna ? "bg-amber-50/60" : "bg-blue-50/60"}`}>
                        <span className="text-[11px] text-muted-foreground w-4 text-right">{idx + 1}.</span>
                        <button
                          type="button"
                          onClick={() => updateNaloga(idx, "obvezno", !isObvezna)}
                          className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 transition ${isObvezna ? "bg-amber-200 text-amber-900 hover:bg-amber-300" : "bg-blue-200 text-blue-900 hover:bg-blue-300"}`}
                          title={isObvezna ? "Klikni za 'Dodatna'" : "Klikni za 'Obvezna'"}
                          data-testid={`toggle-obvezno-2-${idx}`}
                        >
                          {isObvezna ? "OBV" : "DOD"}
                        </button>
                        <Input
                          value={n.naslov}
                          onChange={(e) => updateNaloga(idx, "naslov", e.target.value)}
                          placeholder="Naslov podnaloge"
                          className="h-8 text-xs flex-1"
                        />
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          value={n.ure ?? ""}
                          onChange={(e) => updateNaloga(idx, "ure", e.target.value)}
                          placeholder="ure"
                          className="h-8 text-xs w-16"
                          title="Predvidene ure"
                        />
                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => removeNaloga(idx)}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setItemDialogOpen(false)}>Prekliči</Button>
              <Button type="submit">{editingItem ? "Posodobi" : "Ustvari"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteItemConfirm} onOpenChange={(o) => { if (!o) setDeleteItemConfirm(null); }}>
        <DialogContent className="max-w-sm mx-4" data-testid="delete-item-confirm">
          <DialogHeader>
            <DialogTitle>Izbris postavke</DialogTitle>
            <DialogDescription>
              Postavka "{deleteItemConfirm?.naslov}" bo trajno izbrisana skupaj z vsemi napredki pripravnikov. Akcije ni mogoče razveljaviti.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteItemConfirm(null)}>Prekliči</Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteItem}
              data-testid="confirm-delete-item"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Izbriši
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dodaj pripravnika dialog */}
      <Dialog open={addPripDlg} onOpenChange={setAddPripDlg}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Dodaj pripravnika</DialogTitle>
            <DialogDescription>
              Izberite obstoječega člana in ga pretvorite v pripravnika. Član bo dobil dostop do plana izobraževanja.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 flex-1 min-h-0 flex flex-col">
            <Input
              value={eligibleSearch}
              onChange={(e) => setEligibleSearch(e.target.value)}
              placeholder="Iskanje po imenu, priimku ali izkaznici..."
              data-testid="pripravnik-search"
            />
            {eligibleLoading ? (
              <div className="text-sm text-muted-foreground text-center py-6">Nalagam ...</div>
            ) : (
              <ScrollArea className="flex-1 min-h-0 border rounded max-h-96">
                <div className="divide-y">
                  {eligibleMembers
                    .filter(m => {
                      if (!eligibleSearch.trim()) return true;
                      const q = eligibleSearch.toLowerCase().trim();
                      return `${m.ime} ${m.priimek} ${m.lovska_izkaznica || ''}`.toLowerCase().includes(q);
                    })
                    .map(m => (
                      <div key={m.id} className="flex items-center justify-between p-2 hover:bg-muted/40">
                        <div>
                          <div className="text-sm font-medium">{m.ime} {m.priimek}</div>
                          {m.lovska_izkaznica && (
                            <div className="text-xs text-muted-foreground">Izkaznica: {m.lovska_izkaznica}</div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => promoteToPripravnik(m.id, `${m.ime} ${m.priimek}`)}
                          data-testid={`promote-${m.id}`}
                        >
                          Pretvori v pripravnika
                        </Button>
                      </div>
                    ))}
                  {eligibleMembers.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-6">
                      Ni članov na voljo za pretvorbo.
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPripDlg(false)}>Zapri</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainingPage;
